import React from 'react';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import Permitted from 'components/core/Permitted.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import PERM from 'constants/core/perm.const';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import useTip from 'hooks/useTip.hook';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import linkTo from 'utils/links/linkTo.util';

import CN from './EditKpisInstructionsModal.module.scss';

const EditKpisInstructionsModal = ({ tag }) => {
  const { isDarkMode } = useTheme();
  const salonId = useCurrentSalonId();
  const { val: isOpen, tip: toggle, nay: hide } = useTip(false);

  return (
    <div
      data-bem="EditKpisInstructionsModal"
      data-tag={fromAnyToTrimmed(tag)}
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.buttonContainer}>
        <Button variant="icon" onClick={toggle}>
          <div data-bem="KpiSummary__button" className={CN.buttonInner}>
            <Icon variant="questionSolid" width="lg" />
            <span className={CN.text}>How to edit KPIs</span>
          </div>
        </Button>
      </div>

      <SalonModal tag="TimeOffSplashUpgradePlan" isOpen={isOpen} variant="medium">
        <SalonModal.Header>
          How to reorder/remove/add KPIs
          <Button variant="empty" onClick={hide}>
            <Icon variant="close" />
          </Button>
        </SalonModal.Header>
        <SalonModal.Body>
          <Permitted value={PERM.canEditKpisAllStaff}>
            <div className={CN.modalContainer}>
              <ul className={CN.list}>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="drag" width="lg" variantInvertDark />
                  </div>
                  Click on the 6 dots to re-order your KPIs with drag & drop
                </li>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="info" width="md" variantInvertDark />
                  </div>
                  Click on the {S.quoteOpen}I{S.quoteClose} icon to see how the KPI is calculated
                </li>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="delete" width="lg" variantInvertDark />
                  </div>
                  Click on the bin icon to remove the KPI
                </li>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="plus" width="lg" />
                  </div>
                  Click on {S.quoteOpen}Add KPI{S.quoteClose} at the bottom of each persons table and choose from 30+
                  KPIs
                </li>
              </ul>

              <span className={CN.dot}>
                Customise the KPI list for each staff member - these can be different per person
              </span>
              <span className={CN.dot}>Customise the Business KPIs that you and other managers want to track</span>
              <span className={CN.dot}>
                Go to
                <NavLink
                  to={linkTo({
                    pattern: LINK.settingsPermissions,
                    params: { salonId },
                    fault: '#',
                  })}
                >
                  <span className={CN.link}>Staff Access Permissions</span>
                </NavLink>
                to choose which staff members can view their own KPIs
              </span>
            </div>
          </Permitted>
          <Permitted value={PERM.canEditKpisPersonal}>
            <div className={CN.modalContainer}>
              <ul className={CN.list}>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="drag" width="lg" variantInvertDark />
                  </div>
                  Click on the 6 dots to re-order your KPIs with drag & drop
                </li>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="info" width="md" variantInvertDark />
                  </div>
                  Click on the {S.quoteOpen}I{S.quoteClose} icon to see how the KPI is calculated
                </li>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="delete" width="lg" variantInvertDark />
                  </div>
                  Click on the bin icon to remove the KPI
                </li>
                <li>
                  <div className={CN.buttonIcon}>
                    <Icon variant="plus" width="lg" />
                  </div>
                  Click on {S.quoteOpen}Add KPI{S.quoteClose} at the bottom of each persons table and choose from 30+
                  KPIs
                </li>
              </ul>
              <span className={CN.dot}>Customise the KPIs you want to track</span>
            </div>
          </Permitted>
        </SalonModal.Body>
      </SalonModal>
    </div>
  );
};

export default EditKpisInstructionsModal;
